import React, { useEffect, useState } from 'react';
import './Gallery.css';

export type GalleryProps = {
  images?: string[]; // filenames relative to /img/gallery/
  title?: string;
  initialLimit?: number; // Number of images to show initially (default 9)
  showLoadMore?: boolean; // Whether to show load more button (default true)
  placeholderTextColor?: string; // Text color for placeholder (default: inherit)
  counterTextColor?: string; // Text color for image counter (default: inherit)
};

const defaultImages = [
  "sb2024/HGH_6410.jpg","sb2024/HGH_6432.jpg","sb2024/HGH_6581.jpg","sb2024/HGH_6629.jpg","sb2024/HGH_6649.jpg","sb2024/HGH_6660.jpg","sb2024/HGH_6760.jpg","sb2024/HGH_6768.jpg","sb2024/HGH_6778.jpg","sb2024/HGH_6782.jpg","sb2024/HGH_6807.jpg","sb2024/HGH_6832.jpg","sb2024/HGH_6869.jpg","sb2025/HGH_7298.jpg","sb2025/HGH_7317.jpg","sb2025/IMG_4831.jpg","sb2025/IMG_4833.jpg","sb2025/IMG_4835.jpg","sb2025/IMG_7076.jpg","sb2025/IMG_7087.jpg","sb2025/IMG_8908.jpg"
];

const Gallery: React.FC<GalleryProps> = ({
  images = defaultImages,
  title = 'Image Gallery',
  initialLimit = 9,
  showLoadMore = true,
  placeholderTextColor,
  counterTextColor
}) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [displayedImages, setDisplayedImages] = useState<string[]>(images.slice(0, initialLimit));
  const [currentIndex, setCurrentIndex] = useState(Math.min(initialLimit, images.length));
  const [isLoading, setIsLoading] = useState(false);
  const imagesPerLoad = 9;

  // Reset pagination state when the selected gallery dataset changes.
  useEffect(() => {
    const nextIndex = Math.min(initialLimit, images.length);
    setDisplayedImages(images.slice(0, initialLimit));
    setCurrentIndex(nextIndex);
    setSelected(null);
  }, [images, initialLimit]);

  const loadMoreImages = () => {
    if (currentIndex >= images.length || isLoading) return;
    
    setIsLoading(true);
    
    const nextImages = images.slice(currentIndex, currentIndex + imagesPerLoad);
    setDisplayedImages(prev => [...prev, ...nextImages]);
    setCurrentIndex(prev => prev + imagesPerLoad);
    setIsLoading(false);
  };

  const hasMoreImages = currentIndex < images.length;

  // Bind Escape key to close modal
  useEffect(() => {
    if (!selected) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelected(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selected]);

  return (
    <div className="container my-5" id="gallery">
      {title ? <h2 className="text-center mb-4">{title}</h2> : null}
      {images.length === 0 ? (
        <div className="text-center py-5">
          <p className="fs-5" style={{ color: placeholderTextColor || '#212529' }}>Photos coming soon!</p>
        </div>
      ) : (
        <div className="row justify-content-center">
          {displayedImages.map((img, idx) => (
            <div className="col-md-4 col-sm-6 mb-4" key={img}>
              <img
                src={`/img/gallery/${img}`}
                alt={img}
                className="img-fluid rounded gallery-thumb"
                style={{ cursor: 'pointer' }}
                onClick={() => setSelected(img)}
              />
            </div>
          ))}
        </div>
      )}
      
      {/* Load More Section */}
      {showLoadMore && hasMoreImages && (
        <div className="text-center mt-4">
          <button
            className="btn btn-primary btn-lg load-more-btn"
            onClick={loadMoreImages}
            disabled={isLoading}
          >
            {isLoading ? (
              'Loading...'
            ) : (
              <>
                <i className="fas fa-images me-2" aria-hidden></i>
                {`Load More Images (${Math.min(imagesPerLoad, images.length - currentIndex)} remaining)`}
              </>
            )}
          </button>
        </div>
      )}

      {/* Image Counter */}
      {showLoadMore && images.length > 0 && (
        <div className="text-center mt-3">
          <small style={{ color: counterTextColor || '#212529' }}>
            Showing {displayedImages.length} of {images.length} images
          </small>
        </div>
      )}

      {selected && (
        <div className="gallery-modal" onClick={() => setSelected(null)}>
          <div className="gallery-modal-content" onClick={e => e.stopPropagation()}>
            <img src={`/img/gallery/${selected}`} alt={selected} className="img-fluid rounded gallery-modal-img" />
            <button className="btn btn-light mt-3" onClick={() => setSelected(null)}>
              <i className="fas fa-times me-2" aria-hidden></i>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;